import React, { useState, useMemo } from 'react';
import { getOnboardingUrl } from "../../utils/portalUrl";
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  FaCalendarCheck, FaUsers, FaGlobe, FaVideo,
  FaBell, FaUserFriends, FaCreditCard, FaCalendarAlt,
  FaBullhorn, FaTag, FaImages, FaIdCard, FaClipboardList,
  FaUserShield, FaQrcode, FaMobileAlt, FaSearch
} from 'react-icons/fa';
import Navbar from '../../Components/Navbar/Navbar';
import SEO from '../../Components/SEO/SEO';
import { GUIDES_META } from '../../seo/pageMeta';
import Footer from '../../Components/Footer/Footer';
import styles from './GuidePage.module.css';
import { isHiddenFeatureKey, TEMP_CRM_PIVOT } from '../../config/tempCrmPivot';

const GUIDES = [
  {
    key: 'booking',       path: '/features/online-booking', Icon: FaCalendarCheck, color: '#FE4E00',
    category: 'booking', stepCount: 5
  },
  {
    key: 'appointments',  path: '/features/appointments',   Icon: FaCalendarAlt,   color: '#FE4E00',
    category: 'booking', stepCount: 6
  },
  {
    key: 'crm',           path: '/features/crm',            Icon: FaUsers,         color: '#203052',
    category: 'clients', stepCount: 5
  },
  {
    key: 'notifications', path: '/features/notifications',  Icon: FaBullhorn,      color: '#203052',
    category: 'clients', stepCount: 5
  },
  {
    key: 'reminders',     path: '/features/reminders',      Icon: FaBell,          color: '#f59e0b',
    category: 'clients', stepCount: 5
  },
  {
    key: 'team',          path: '/features/team',           Icon: FaUserFriends,   color: '#10b981',
    category: 'business', stepCount: 5
  },
  {
    key: 'website',       path: '/features/website',        Icon: FaGlobe,         color: '#0ea5e9',
    category: 'business', stepCount: 5
  },
  {
    key: 'profile',       path: '/features/profile',        Icon: FaIdCard,        color: '#8b5cf6',
    category: 'business', stepCount: 6
  },
  {
    key: 'gallery',       path: '/features/gallery',        Icon: FaImages,        color: '#0ea5e9',
    category: 'business', stepCount: 5
  },
  {
    key: 'zoom',          path: '/features/zoom',           Icon: FaVideo,         color: '#2d8cff',
    category: 'business', stepCount: 5
  },
  {
    key: 'payments',      path: '/features/payments',       Icon: FaCreditCard,    color: '#8b5cf6',
    category: 'finance',  stepCount: 5
  },
  {
    key: 'discounts',     path: '/features/discounts',      Icon: FaTag,           color: '#10b981',
    category: 'finance',  stepCount: 5
  },
  {
    key: 'services',      path: '/features/services',       Icon: FaClipboardList, color: '#FE4E00',
    category: 'business', stepCount: 7
  },
  {
    key: 'calendar',      path: '/features/calendar',       Icon: FaCalendarAlt,   color: '#0ea5e9',
    category: 'booking',  stepCount: 5
  },
  {
    key: 'roles',         path: '/features/roles',          Icon: FaUserShield,    color: '#10b981',
    category: 'business', stepCount: 5
  },
  {
    key: 'qr',            path: '/features/qr',             Icon: FaQrcode,        color: '#203052',
    category: 'business', stepCount: 5
  },
  {
    key: 'app',           path: '/features/app',            Icon: FaMobileAlt,     color: '#8b5cf6',
    category: 'business', stepCount: 5
  },
];

const FILTER_CATEGORIES = [
  { id: 'all',      labelKey: 'guides.filterAll' },
  ...(TEMP_CRM_PIVOT ? [] : [{ id: 'booking', labelKey: 'guides.filterBooking' }]),
  { id: 'clients',  labelKey: TEMP_CRM_PIVOT ? 'tempPivot.guides.filterProducts' : 'guides.filterClients' },
  { id: 'business', labelKey: 'guides.filterBusiness' },
  { id: 'finance',  labelKey: 'guides.filterFinance' },
];

const GuidePage = () => {
  const { t } = useTranslation();
  const [query, setQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return GUIDES.filter(g => {
      if (isHiddenFeatureKey(g.key)) return false;
      const matchesCategory = activeFilter === 'all' || g.category === activeFilter;
      const matchesQuery = !q || [
        t(`features.cards.${g.key}.title`),
        t(`features.cards.${g.key}.desc`),
        ...Array.from({ length: g.stepCount }, (_, i) => i + 1).map(n => t(`features.${g.key}.g${n}t`)),
      ].some(str => str.toLowerCase().includes(q));
      return matchesCategory && matchesQuery;
    });
  }, [query, activeFilter, t]);

  return (
    <div className={styles.page}>
      <SEO {...GUIDES_META} />
      <Navbar />

      <section className={styles.hero}>
        <div className="container">
          <h1 data-aos="fade-up">{t('guides.heroTitle')}</h1>
          <p data-aos="fade-up" data-aos-delay="80">{t('guides.heroSubtitle')}</p>
          <div className={styles.searchWrap} data-aos="fade-up" data-aos-delay="160">
            <FaSearch className={styles.searchIcon} />
            <input
              type="text"
              className={styles.searchInput}
              placeholder={t('guides.searchPlaceholder')}
              value={query}
              onChange={e => setQuery(e.target.value)}
            />
          </div>
        </div>
      </section>

      <section className={styles.content}>
        <div className="container">
          <div className={styles.filterRow}>
            {FILTER_CATEGORIES.map(({ id, labelKey }) => (
              <button
                key={id}
                className={`${styles.filterChip} ${activeFilter === id ? styles.filterChipActive : ''}`}
                onClick={() => setActiveFilter(id)}
                type="button"
              >
                {t(labelKey)}
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <p className={styles.noResults}>{t('guides.noResults')}</p>
          ) : (
            <div className={styles.grid}>
              {filtered.map(({ key, path, Icon, color, stepCount }) => (
                <div key={key} className={styles.card} data-aos="fade-up">
                  <div className={styles.cardTop}>
                    <div className={styles.cardIcon} style={{ backgroundColor: color + '18', color }}>
                      <Icon />
                    </div>
                    <h3>{t(`features.cards.${key}.title`)}</h3>
                    <p>{t(`features.cards.${key}.desc`)}</p>
                  </div>

                  <div className={styles.stepPreview}>
                    {Array.from({ length: Math.min(stepCount, 3) }, (_, i) => i + 1).map(n => (
                      <div key={n} className={styles.stepPreviewItem}>
                        <span className={styles.stepPreviewNum} style={{ color }}>{n}</span>
                        <span className={styles.stepPreviewText}>{t(`features.${key}.g${n}t`)}</span>
                      </div>
                    ))}
                    {stepCount > 3 && (
                      <div className={styles.moreSteps} style={{ color }}>
                        +{stepCount - 3} {t('guides.stepsLabel').toLowerCase()}
                      </div>
                    )}
                  </div>

                  <Link
                    to={`${path}#guide`}
                    className={styles.cardCta}
                    style={{ backgroundColor: color }}
                    onClick={() => setTimeout(() => {
                      const el = document.getElementById('guide');
                      if (el) el.scrollIntoView({ behavior: 'smooth' });
                    }, 300)}
                  >
                    {t('guides.viewGuide')} →
                  </Link>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className={styles.ctaSection}>
        <div className="container">
          <h2>{t('features.common.ctaTitle')}</h2>
          <p>{t('features.common.ctaSubtitle')}</p>
          <a
            href={getOnboardingUrl("cta")}
            className={styles.ctaButton}
          >
            
            {t('features.common.cta')}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GuidePage;
